import React from "react";
import { Reveal, SectionLabel } from "../components/primitives";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";

const ENTITIES = [
  {
    logo: "/brand/folks-motor.png",
    logoClass: "h-14",
    code: "01",
    role: "The Operator",
    title: "Folks Motor",
    short: "India's first hybrid-electric retrofit car company.",
    long: "80+ service dealers, 25 distributors and 5 C&F agents across 5 cities. Folks Motor is a live distribution channel and provides ground-truth Indian performance data for portfolio hardware companies.",
    contributions: ["Customer pilots & first revenue", "Distribution via 80+ dealers", "Real-world performance data", "Guaranteed end-of-life battery pipeline"],
    href: "https://www.folksmotor.com/",
  },
  {
    logo: "/brand/folks-finance.png",
    logoClass: "h-10",
    code: "02",
    role: "The Capital Enabler",
    title: "Folks Finance",
    short: "NBFC solving hardware's working-capital constraint.",
    long: "Bill discounting, distributor financing and project loans. Targets a ₹250 Cr loan book by 2030. Doubles a portfolio company's effective capital without dilution.",
    contributions: ["Bill discounting", "Working capital lines", "Distributor financing", "Project finance & equipment loans"],
  },
  {
    logo: "/brand/folks-funds.png",
    logoClass: "h-10",
    code: "03",
    role: "The Orchestrator",
    title: "Folks Funds",
    short: "SEBI-registered AIF Cat II. ₹500 Cr target.",
    long: "Deploys equity and orchestrates Motor + Finance to compound portfolio company returns. Bottom-up sector picking, ESG alpha as a value driver, structured value creation.",
    contributions: ["Seed→Series A equity", "Strategic global network", "Board governance & KPIs", "Hire-to-scale & exit planning"],
  },
];

export default function Ecosystem() {
  return (
    <div data-testid="ecosystem-page">
      <section className="pt-20 pb-16 lg:pt-32 lg:pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <SectionLabel>Ecosystem</SectionLabel>
            <h1 className="font-display font-bold tracking-[-0.035em] leading-[0.94] text-5xl sm:text-7xl lg:text-[104px] text-[#111A15]">
              Three entities.<br /><span className="text-[#C44900]">One flywheel.</span>
            </h1>
            <p className="mt-10 max-w-2xl text-[17px] text-[#4A5E51] leading-relaxed">
              Folks Motor generates distribution and intelligence. Folks Finance solves working capital. Folks Funds provides equity and network. Portfolio companies grow faster — and the cycle accelerates.
            </p>
          </div>
          <div className="lg:col-span-5 relative lg:-ml-6 xl:-ml-12 lg:pr-8 xl:pr-16">
            <div className="relative max-w-[480px] mx-auto">
              <img
                src="/brand/folks-ecosystem.png"
                alt="Folks ecosystem — Folks Motor, Folks Finance and Folks Funds"
                className="w-full h-auto select-none mix-blend-multiply"
                style={{ filter: "drop-shadow(0 1px 0 rgba(17,26,21,0.04))" }}
                draggable="false"
                data-testid="ecosystem-hero-image"
              />
              <span className="block mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] text-center">— The Folks ecosystem</span>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-[#E5E5E0] bg-[#E8ECE9]/40 py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {ENTITIES.map((e, idx) => {
              const card = (
                <div data-testid={`ecosystem-${e.title.toLowerCase().replace(/\s+/g, "-")}-card`} className="bg-white border border-[#E5E5E0] p-8 lg:p-10 h-full flex flex-col group">
                  <div className="flex items-center justify-between mb-10 h-16">
                    <img src={e.logo} alt={e.title} className={`${e.logoClass} w-auto object-contain mix-blend-multiply`} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51]">— {e.code}</span>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C44900] mb-3">{e.role}</p>
                  <h2 className="font-display font-semibold tracking-tight text-3xl lg:text-4xl text-[#111A15] mb-3 flex items-center gap-2">
                    {e.title}
                    {e.href && <ArrowUpRight className="w-5 h-5 text-[#4A5E51] group-hover:text-[#C44900] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />}
                  </h2>
                  <p className="text-[16px] text-[#111A15] mb-4 font-medium">{e.short}</p>
                  <p className="text-[14px] text-[#4A5E51] leading-relaxed mb-8">{e.long}</p>
                  <ul className="mt-auto border-t border-[#E5E5E0] pt-5 space-y-2.5">
                    {e.contributions.map((c) => (
                      <li key={c} className="flex items-start gap-2 text-[13px] text-[#4A5E51]">
                        <span className="text-[#C44900] mt-1">●</span> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              );
              return (
                <Reveal key={e.title} delay={idx * 100}>
                  {e.href ? (
                    <a href={e.href} target="_blank" rel="noopener noreferrer" data-testid={`ecosystem-${e.title.toLowerCase().replace(/\s+/g, "-")}-link`} className="block h-full">{card}</a>
                  ) : card}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Flywheel diagram (CSS) */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <SectionLabel>How the flywheel turns</SectionLabel>
          <h3 className="font-display font-medium tracking-tight text-3xl sm:text-4xl text-[#111A15] mb-12 max-w-2xl leading-tight">
            Integrated proprietary technology with infrastructure to create supply chain as an asset class.
          </h3>

          <ol className="space-y-px bg-[#E5E5E0]">
            {[
              "Folks Motor generates distribution + intelligence.",
              "Folks Finance solves working capital.",
              "Folks Funds provides equity + network.",
              "Portfolio companies grow faster.",
              "The cycle accelerates.",
            ].map((step, i, arr) => (
              <Reveal key={i} delay={i * 70}>
                <div className="bg-white p-6 lg:p-8 flex items-center gap-6 lg:gap-10">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C44900] w-12 shrink-0">— 0{i + 1}</span>
                  <p className="font-display font-medium text-xl sm:text-2xl text-[#111A15] leading-snug flex-1">{step}</p>
                  {i < arr.length - 1 ? <ArrowDown className="w-5 h-5 text-[#9DB0A3]" /> : <ArrowRight className="w-5 h-5 text-[#C44900]" />}
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}
